<template>
  <div class="modal fade" id="uploadDetailsModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="mdi mdi-file-document-outline me-2"></i>
            Upload Details - {{ uploadDetails.fileName }}
          </h4>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <!-- Upload Summary -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="card border">
                <div class="card-header bg-light">
                  <h5 class="mb-0">Upload Summary</h5>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6">
                      <table class="table table-sm table-borderless">
                        <tr>
                          <td><strong>Upload Date:</strong></td>
                          <td>{{ uploadDetails.dateCreated }}</td>
                        </tr>
                        <tr>
                          <td><strong>Revenue Stream:</strong></td>
                          <td>{{ uploadDetails.revenueStream }}</td>
                        </tr>
                        <tr>
                          <td><strong>File Name:</strong></td>
                          <td>{{ uploadDetails.fileName }}</td>
                        </tr>
                        <tr>
                          <td><strong>Status:</strong></td>
                          <td>
                            <span class="badge" :class="getStatusClass(uploadDetails.status)">
                              {{ uploadDetails.status }}
                            </span>
                          </td>
                        </tr>
                      </table>
                    </div>
                    <div class="col-md-6">
                      <table class="table table-sm table-borderless">
                        <tr>
                          <td><strong>Total Records:</strong></td>
                          <td>{{ uploadDetails.totalRecords }}</td>
                        </tr>
                        <tr>
                          <td><strong>Successful:</strong></td>
                          <td class="text-success fw-bold">{{ uploadDetails.successful }}</td>
                        </tr>
                        <tr>
                          <td><strong>Failed:</strong></td>
                          <td class="text-danger fw-bold">{{ uploadDetails.failed }}</td>
                        </tr>
                        <tr>
                          <td><strong>Total Amount:</strong></td>
                          <td class="fw-bold">{{ formatPrice(uploadDetails.totalAmount) }}</td>
                        </tr>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Tabs for different views -->
          <ul class="nav nav-tabs" role="tablist">
            <li class="nav-item">
              <a class="nav-link active" data-bs-toggle="tab" href="#successful-records" role="tab">
                <i class="mdi mdi-check-circle me-1"></i>
                Successful Records ({{ mockSuccessfulRecords.length }})
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#failed-records" role="tab">
                <i class="mdi mdi-alert-circle me-1"></i>
                Failed Records ({{ mockFailedRecords.length }})
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#penalties-applied" role="tab">
                <i class="mdi mdi-currency-usd me-1"></i>
                Penalties Applied ({{ mockPenaltyRecords.length }})
              </a>
            </li>
          </ul>

          <div class="tab-content mt-3">
            <!-- Successful Records Tab -->
            <div class="tab-pane fade show active" id="successful-records" role="tabpanel">
              <div class="table-responsive">
                <table class="table table-hover table-sm">
                  <thead class="table-success">
                  <tr>
                    <th>Row</th>
                    <th>Bill No</th>
                    <th>Customer Name</th>
                    <th>Phone</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="record in mockSuccessfulRecords" :key="record.id">
                    <td>{{ record.row }}</td>
                    <td class="fw-bold">{{ record.billNo }}</td>
                    <td>{{ record.customerName }}</td>
                    <td>{{ record.phone }}</td>
                    <td>{{ formatPrice(record.amount) }}</td>
                    <td>
                      <span class="badge bg-success">{{ record.status }}</span>
                    </td>
                    <td>{{ record.dateCreated }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Failed Records Tab -->
            <div class="tab-pane fade" id="failed-records" role="tabpanel">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6>Failed Records</h6>
                <button v-if="mockFailedRecords.length > 0"
                        @click="retryFailedRecords"
                        class="btn btn-warning btn-sm">
                  <i class="mdi mdi-replay me-1"></i>
                  Retry All Failed
                </button>
              </div>

              <div class="table-responsive">
                <table class="table table-hover table-sm">
                  <thead class="table-danger">
                  <tr>
                    <th>Row</th>
                    <th>Customer Name</th>
                    <th>Amount</th>
                    <th>Error Details</th>
                    <th>Actions</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="record in mockFailedRecords" :key="record.id">
                    <td>{{ record.row }}</td>
                    <td>{{ record.customerName }}</td>
                    <td>{{ formatPrice(record.amount) }}</td>
                    <td>
                      <ul class="list-unstyled mb-0">
                        <li v-for="error in record.errors" :key="error" class="text-danger">
                          <small>• {{ error }}</small>
                        </li>
                      </ul>
                    </td>
                    <td>
                      <button @click="retryRecord(record)" class="btn btn-outline-warning btn-sm">
                        <i class="mdi mdi-replay"></i>
                      </button>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Penalties Applied Tab -->
            <div class="tab-pane fade" id="penalties-applied" role="tabpanel">
              <div class="table-responsive">
                <table class="table table-hover table-sm">
                  <thead class="table-warning">
                  <tr>
                    <th>Bill No</th>
                    <th>Customer Name</th>
                    <th>Original Amount</th>
                    <th>Penalty Type</th>
                    <th>Penalty Amount</th>
                    <th>Total Amount</th>
                    <th>Due Date</th>
                    <th>Days Overdue</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="record in mockPenaltyRecords" :key="record.id">
                    <td class="fw-bold">{{ record.billNo }}</td>
                    <td>{{ record.customerName }}</td>
                    <td>{{ formatPrice(record.originalAmount) }}</td>
                    <td>
                      <span class="badge bg-warning">{{ record.penaltyType }}</span>
                    </td>
                    <td class="text-warning fw-bold">{{ formatPrice(record.penaltyAmount) }}</td>
                    <td class="fw-bold">{{ formatPrice(record.totalAmount) }}</td>
                    <td>{{ record.dueDate }}</td>
                    <td>
                      <span class="badge bg-danger">{{ record.daysOverdue }} days</span>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <div class="d-flex justify-content-between w-100">
            <div>
              <button @click="downloadDetailedReport" class="btn btn-success">
                <i class="mdi mdi-download me-1"></i>
                Download Detailed Report
              </button>
            </div>
            <div>
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import moment from "moment";

export default {
  name: "UploadDetailsModal",
  props: {
    uploadDetails: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      // Mock data for demonstration
      mockSuccessfulRecords: [
        {
          id: 1,
          row: 1,
          billNo: 'SBP250001',
          customerName: 'John Doe Enterprises',
          phone: '+254701234567',
          amount: 15000,
          status: 'ACTIVE',
          dateCreated: '2025-01-15 10:30:15'
        },
        {
          id: 2,
          row: 2,
          billNo: 'SBP250002',
          customerName: 'Mary\'s Beauty Salon',
          phone: '+254702345678',
          amount: 12000,
          status: 'ACTIVE',
          dateCreated: '2025-01-15 10:30:16'
        },
        {
          id: 3,
          row: 3,
          billNo: 'SBP250003',
          customerName: 'Tech Solutions Ltd',
          phone: '+254703456789',
          amount: 25000,
          status: 'ACTIVE',
          dateCreated: '2025-01-15 10:30:17'
        },
        {
          id: 4,
          row: 4,
          billNo: 'SBP250004',
          customerName: 'Green Valley Restaurant',
          phone: '+254704567890',
          amount: 18000,
          status: 'ACTIVE',
          dateCreated: '2025-01-15 10:30:18'
        },
        {
          id: 5,
          row: 5,
          billNo: 'SBP250005',
          customerName: 'City Hardware Store',
          phone: '+254705678901',
          amount: 22000,
          status: 'ACTIVE',
          dateCreated: '2025-01-15 10:30:19'
        }
      ],

      mockFailedRecords: [
        {
          id: 1,
          row: 8,
          customerName: 'Invalid Business ABC',
          amount: 15000,
          errors: [
            'Missing phone number',
            'Invalid email format',
            'Business registration number not found'
          ]
        },
        {
          id: 2,
          row: 15,
          customerName: 'Duplicate Entry Ltd',
          amount: 12000,
          errors: [
            'Duplicate customer ID',
            'Bill already exists for this period'
          ]
        },
        {
          id: 3,
          row: 23,
          customerName: 'Incomplete Data Co',
          amount: 8000,
          errors: [
            'Missing required documents',
            'Invalid location code'
          ]
        }
      ],

      mockPenaltyRecords: [
        {
          id: 1,
          billNo: 'SBP240890',
          customerName: 'Late Payer Enterprises',
          originalAmount: 15000,
          penaltyType: 'PERCENTAGE',
          penaltyAmount: 750,
          totalAmount: 15750,
          dueDate: '2024-12-15',
          daysOverdue: 32
        },
        {
          id: 2,
          billNo: 'SBP240891',
          customerName: 'Overdue Business Ltd',
          originalAmount: 20000,
          penaltyType: 'FIXED',
          penaltyAmount: 1000,
          totalAmount: 21000,
          dueDate: '2024-11-30',
          daysOverdue: 47
        },
        {
          id: 3,
          billNo: 'SBP240892',
          customerName: 'Delayed Payment Co',
          originalAmount: 12000,
          penaltyType: 'PERCENTAGE',
          penaltyAmount: 600,
          totalAmount: 12600,
          dueDate: '2024-12-20',
          daysOverdue: 27
        }
      ]
    }
  },
  methods: {
    formatPrice(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(value);
    },

    getStatusClass(status) {
      switch (status) {
        case 'COMPLETED':
          return 'bg-success';
        case 'PROCESSING':
          return 'bg-warning';
        case 'FAILED':
          return 'bg-danger';
        default:
          return 'bg-secondary';
      }
    },

    retryFailedRecords() {
      this.$emit('retry-failed-records', this.uploadDetails.id);

      // Close modal after a brief delay
      setTimeout(() => {
        const modalElement = document.getElementById('uploadDetailsModal');
        if (modalElement) {
          const bootstrapModal = bootstrap.Modal.getInstance(modalElement);
          if (bootstrapModal) {
            bootstrapModal.hide();
          }
        }
      }, 1000);
    },

    retryRecord(record) {
      // In real implementation, this would retry a specific record
      console.log('Retrying record:', record);

      // Remove from failed records (simulate success)
      const index = this.mockFailedRecords.findIndex(r => r.id === record.id);
      if (index > -1) {
        this.mockFailedRecords.splice(index, 1);
      }
    },

    downloadDetailedReport() {
      // Simulate downloading detailed report
      const fileName = `${this.uploadDetails.fileName}_detailed_report.xlsx`;
      console.log('Downloading detailed report:', fileName);

      // In real implementation, this would trigger actual file download
      alert(`Downloading ${fileName}...`);
    }
  }
}
</script>

<style scoped>
.table-sm td,
.table-sm th {
  padding: 0.5rem;
  font-size: 0.875rem;
}

.nav-tabs .nav-link {
  border-bottom: 2px solid transparent;
}

.nav-tabs .nav-link.active {
  border-bottom-color: #0d6efd;
  font-weight: 600;
}

.badge {
  font-size: 0.75rem;
}

.table-borderless td,
.table-borderless th {
  border: none;
}

.list-unstyled {
  margin-bottom: 0;
}

.tab-content {
  min-height: 400px;
  max-height: 500px;
  overflow-y: auto;
}

.modal-xl {
  max-width: 1200px;
}
</style>